import React, { useState, useRef, useEffect } from "react";
import { Container, Grid, Box, Button, Typography } from "@mui/material";
import GoogleIcon from "../../assets/google-button-icon.png";
import AppleIcon from "../../assets/apple-button-icon.png";

const AuthComponent = ({
  theme,
  pair,
  pairingCode,
  handleEmailPasswordSignIn,
  handleGoogleSignIn,
  handleAppleSignIn,
  onCodeComplete, // New callback prop
  loading,
  error,
}) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState(["", "", "", "", "", ""]); // For 6 code inputs
  const inputRefs = useRef([]);
  const isInitialCodeSet = useRef(false); // Ref to track if initial code is set

  // Populate the code inputs when pairingCode is passed (only once)
  useEffect(() => {
    if (pairingCode && !isInitialCodeSet.current) {
      const upperCaseCode = pairingCode.toUpperCase();
      const newCode = upperCaseCode.slice(0, 6).split(""); // Take up to 6 characters
      setCode(newCode);
      console.log("Pairing code set from URL:", newCode);

      // Optionally, focus on the first empty input after populating
      const firstEmptyIndex = newCode.findIndex((digit) => digit === "");
      if (firstEmptyIndex !== -1 && inputRefs.current[firstEmptyIndex]) {
        inputRefs.current[firstEmptyIndex].focus();
      }

      isInitialCodeSet.current = true; // Prevent further updates from pairingCode
    }
  }, [pairingCode]);

  // Function to check if code is complete
  useEffect(() => {
    if (code.every((digit) => digit !== "")) {
      const completeCode = code.join("");
      console.log("Complete code entered:", completeCode);
      onCodeComplete(completeCode); // Notify parent that code is complete
    }
  }, [code, onCodeComplete]);

  // Handle individual code input
  const handleCodeInput = (index, value) => {
    const regex = /^[a-zA-Z0-9]$/;
    if (!regex.test(value) && value.length === 1) return;

    const newCode = [...code];
    newCode[index] = value.toUpperCase();
    setCode(newCode);

    // Move focus to the next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Handle paste event
  const handlePaste = (index, e) => {
    e.preventDefault(); // Prevent default paste behavior
    const pastedData = e.clipboardData.getData("text").slice(0, 6); // Get up to 6 characters
    const newCode = [...code];

    // Populate inputs starting from the current index
    pastedData.split("").forEach((char, i) => {
      if (index + i < 6) {
        newCode[index + i] = char.toUpperCase();
      }
    });

    setCode(newCode);

    // Move focus to the last populated input
    const lastIndex = Math.min(index + pastedData.length - 1, 5);
    inputRefs.current[lastIndex]?.focus();
  };

  // Handle backspace event
  const handleBackspace = (index) => {
    const newCode = [...code];
    newCode[index] = ""; // Clear the current input
    setCode(newCode);

    // Move focus to the previous input
    if (index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  return (
    <Container maxWidth="xs">
      <Box sx={{ mt: 4 }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            fontFamily: "basic-sans",
            fontWeight: "bold",
            fontSize: "1.3rem",
          }}
          mb={3}
          align="center"
        >
          {pair ? "Enter TV pairing code & Sign in" : "Sign in"}
        </Typography>

        {/* Display any error messages */}
        {error && (
          <Typography color="error" variant="body2" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        {pair && (
          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={12}>
              <Box
                display="flex"
                justifyContent="space-between" // Spread out the inputs
                gap={1} // Gap between inputs
                mb={4} // Margin bottom
                width="100%" // Full width of container
              >
                {code.map((digit, index) => (
                  <input
                    key={index}
                    type="text"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleCodeInput(index, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Backspace") {
                        handleBackspace(index);
                      }
                    }}
                    onPaste={(e) => handlePaste(index, e)} // Handle paste event
                    ref={(el) => (inputRefs.current[index] = el)}
                    style={{
                      width: "19%",
                      height: "60px",
                      textAlign: "center",
                      fontSize: "1.5rem",
                      fontFamily: "basic-sans",
                      fontWeight: "700",
                      color: "white",
                      backgroundColor: theme.colors.mediumPurple,
                      borderRadius: "12px",
                      border: `0px solid ${theme.colors.darkPurple}`,
                      outline: "none",
                    }}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        )}

        {/* Email and Password Section */}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                paddingLeft: "20px",
                marginBottom: "2px",
                fontSize: "16px",
                fontFamily: "basic-sans",
                borderRadius: "50px",
                outline: "none",
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                paddingLeft: "20px",
                marginBottom: "0px",
                fontSize: "16px",
                fontFamily: "basic-sans",
                borderRadius: "50px",
                outline: "none",
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              onClick={() => handleEmailPasswordSignIn(email, password)}
              variant="contained"
              fullWidth
              sx={{
                backgroundColor: theme.colors.darkBlue,
                border: `1px solid ${theme.colors.darkGray}`,
                color: theme.colors.white,
                borderRadius: "50px",
                padding: "10px 30px",
                marginBottom: "40px",
                fontFamily: "basic-sans",
                fontWeight: "bold",
                textTransform: "none",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: theme.colors.green,
                  border: `1px solid ${theme.colors.green}`,
                  boxShadow: "none",
                },
              }}
              disabled={loading} // Disable button when loading
            >
              {loading ? "Signing In..." : "Sign In with Email"}
            </Button>
          </Grid>
        </Grid>

        {/* Google and Apple Buttons Section */}
        <Box mt={2}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Button
                onClick={handleGoogleSignIn}
                variant="contained"
                fullWidth
                sx={{
                  backgroundColor: theme.colors.paleBlue,
                  border: `1px solid ${theme.colors.paleBlue}`,
                  color: theme.colors.white,
                  borderRadius: "50px",
                  padding: "10px 30px",
                  fontFamily: "basic-sans",
                  fontWeight: "bold",
                  textTransform: "none",
                  boxShadow: "none",
                  position: "relative",
                  "&:hover": {
                    border: `1px solid ${theme.colors.darkGray}`,
                    backgroundColor: theme.colors.darkBlue,
                    boxShadow: "none",
                  },
                }}
                disabled={loading} // Disable button when loading
              >
                <img
                  src={GoogleIcon}
                  alt="Google Icon"
                  style={{
                    width: "20px",
                    position: "absolute",
                    left: "16px",
                  }}
                />

                <Box sx={{ flex: 1, textAlign: "center" }}>
                  {loading ? "Signing In..." : "Sign In with Google"}
                </Box>
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Button
                onClick={handleAppleSignIn}
                variant="contained"
                fullWidth
                sx={{
                  backgroundColor: theme.colors.palePurple,
                  border: `1px solid ${theme.colors.palePurple}`,
                  color: theme.colors.white,
                  borderRadius: "50px",
                  padding: "10px 30px",
                  fontFamily: "basic-sans",
                  fontWeight: "bold",
                  textTransform: "none",
                  boxShadow: "none",
                  position: "relative",
                  "&:hover": {
                    border: `1px solid ${theme.colors.darkGray}`,
                    backgroundColor: theme.colors.darkBlue,
                    boxShadow: "none",
                  },
                }}
                disabled={loading} // Disable button when loading
              >
                <img
                  src={AppleIcon}
                  alt="Apple Icon"
                  style={{
                    width: "20px",
                    position: "absolute",
                    left: "16px",
                  }}
                />

                <Box sx={{ flex: 1, textAlign: "center" }}>
                  {loading ? "Signing In..." : "Sign In with Apple"}
                </Box>
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Container>
  );
};

export default AuthComponent;
